import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import LineCheckCircleIcon from 'LuxKit/Icons/line/LineCheckCircleIcon'
import LinePhoneAltIcon from 'LuxKit/Icons/line/LinePhoneAltIcon'
import LineShieldCheckIcon from 'LuxKit/Icons/line/LineShieldCheckIcon'
import BodyText from 'LuxKit/Typography/BodyText'
import Group from 'components/utils/Group'
import { mediaQueryUp } from 'components/utils/breakpoint'
import { rem } from 'polished'
import type { CSSProperties } from 'react'
import React from 'react'
import { useIntl } from 'react-intl'
import { connect } from 'react-redux'
import { isCarHireInsuranceAvailable } from 'selectors/carHireSelectors'
import styled from 'styled-components'
import eases from 'styles/tools/eases'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import useCarouselAutoPlay from 'hooks/useCarouselAutoPlay'
import PaginationDots from 'LuxKit/PaginationDots/PaginationDots'
import { useScreenSizeUp } from 'hooks/useScreenSize'

const SLIDE_DURATION_MS = 3000
const SLIDE_GAP = 16

const CarHireUSPs = styled.div`
  display: grid;
  gap: ${rem(SLIDE_GAP)};
  overflow: visible;
  margin-top: ${rem(24)};

  ${mediaQueryUp.desktop} {
    margin-top: ${rem(36)};
    grid-template-columns: unset;
    grid-auto-flow: column;
    justify-content: space-between;
  }

  &.carousel {
    grid-template-columns: repeat(4, 100%);
    transition: transform 0.5s ${eases.power2.inOut};

    ${mediaQueryUp.desktop} {
      grid-template-columns: unset;
    }
  }
`

const CarHireUSP = styled.div`
  display: flex;
  flex-direction: column;

  > * + * {
    margin-top: ${rem(8)};
  }

  ${mediaQueryUp.desktop} {
    width: ${rem(310)};
  }
`

const AnimationContainer = styled(VerticalSpacer)`
  overflow: hidden;
  margin-left: -${rem(16)};
  margin-right: -${rem(16)};
  padding-left: ${rem(16)};
  padding-right: ${rem(16)};

  > ${mediaQueryUp.desktop} {
    margin-left: 0;
    margin-right: 0;
    padding-left: 0;
    padding-right: 0;
  }
`

interface Props {
  isCarHireInsuranceAvailable: boolean
}

function CarHireVerticalUSPs(props: Props) {
  const { isCarHireInsuranceAvailable } = props
  const intl = useIntl()

  const usps = [
    {
      title: intl.formatMessage({
        id: 'l8M8YwNh5lKsSrxyy9Ia',
        defaultMessage: 'Free cancellation',
        description: 'USP title for free cancellation on car hire page in the car hire section',
      }),
      description: intl.formatMessage({
        id: 'cqyiSzsI6IlZHaEvRcXJ',
        defaultMessage: 'We offer free cancellation up to 48 hours before your pick-up time.',
        description: 'USP description for free cancellation on car hire page',
      }),
      icon: <LineCheckCircleIcon size={32} />,
      enabled: true,
    },
    {
      title: intl.formatMessage({
        id: 'Az4pTGDCPjCEJAOm4RGq',
        defaultMessage: 'Peace of mind insurance',
        description: 'USP title for insurance on car hire page in the insurance flow',
      }),
      description: intl.formatMessage({
        id: 'LA-7f1o3Q5DuvJ-YUgJy',
        defaultMessage: 'We can provide complete protection insurance options without any excess fees at a great price.',
        description: 'USP description for insurance on car hire page in the car hire section',
      }),
      icon: <LineShieldCheckIcon size={32} />,
      enabled: isCarHireInsuranceAvailable,
    },
    {
      title: intl.formatMessage({
        id: 'FrZMOSBK8OxwtPL8ylmm',
        defaultMessage: 'Trusted rental partners',
        description: 'USP title for trusted partners on car hire page in the car hire section',
      }),
      description: intl.formatMessage({
        id: 'oujLSjO9BGTrSGDc4BeX',
        defaultMessage: 'Discover an extensive range of vehicles from our trusted suppliers.',
        description: 'USP description for trusted partners on car hire page',
      }),
      icon: <LineShieldCheckIcon size={32} />,
      enabled: !isCarHireInsuranceAvailable,
    },
    {
      title: intl.formatMessage({
        id: 'yRn0cJ47Vuh9W04J7uZX',
        defaultMessage: '24/7 Support service',
        description: 'USP title for 24/7 support on car hire page in car hire section',
      }),
      description: intl.formatMessage({
        id: 'clFYvsgNdug3dbRnZDA4',
        defaultMessage: 'Our support team is always on standby, available 24/7.',
        description: 'USP description for 24/7 support on car hire page in the car hire section',
      }),
      icon: <LinePhoneAltIcon size={32} />,
      enabled: true,
    },
  ]

  const enabledUSPs = usps.filter(usp => usp.enabled)

  const isCarouselEnabled = !useScreenSizeUp('desktop')
  const { activeIndex, goToSlide, paused, setPaused } = useCarouselAutoPlay(enabledUSPs.length, SLIDE_DURATION_MS, { enabled: isCarouselEnabled })

  const slideStyle: CSSProperties | undefined = isCarouselEnabled ? {
    transform: `translateX(calc(${-activeIndex} * (100% + ${rem(SLIDE_GAP)})))`,
  } : undefined

  return <AnimationContainer gap={24}>
    <CarHireUSPs className="carousel" style={slideStyle}>
      {enabledUSPs.map((usp) => (
        <Group gap={12} direction="vertical" key={usp.title}>
          {usp.icon}
          <CarHireUSP>
            <BodyText variant="medium" colour="neutral-one" weight="bold">{usp.title}</BodyText>
            <BodyText variant="medium" colour="neutral-two">{usp.description}</BodyText>
          </CarHireUSP>
        </Group>
      ))}
    </CarHireUSPs>

    <CSSBreakpoint max="tablet">
      <PaginationDots
        position="start"
        count={enabledUSPs.length}
        activeIndex={activeIndex}
        slideDuration={SLIDE_DURATION_MS}
        onSelect={goToSlide}
        paused={paused}
        onHoverChange={setPaused}
      />
    </CSSBreakpoint>
  </AnimationContainer>
}

function mapStateToProps(state: App.State) {
  return {
    isCarHireInsuranceAvailable: isCarHireInsuranceAvailable(state),
  }
}

export default connect(mapStateToProps)(CarHireVerticalUSPs)
